import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';

const BranchManagement: React.FC = () => {
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBranch, setNewBranch] = useState({ name: '', code: '', location: '', status: 'Active' });
  const [editingBranch, setEditingBranch] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await apiService.getCrmBranches();
      if (res.success) {
        setBranches(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddBranch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBranch.name.trim()) return;
    setSaving(true);
    try {
      const res = await apiService.createCrmBranch(newBranch);
      if (res.success) {
        setShowAddForm(false);
        setNewBranch({ name: '', code: '', location: '', status: 'Active' });
        fetchData();
      }
    } catch (err) {
      alert('Error creating branch. Branch code or name may already exist.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateBranch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBranch.name.trim()) return;
    setSaving(true);
    try {
      const res = await apiService.updateCrmBranch(editingBranch.id, editingBranch);
      if (res.success) {
        setEditingBranch(null);
        fetchData();
      }
    } catch (err) {
      alert('Error updating branch.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBranch = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete the branch "${name}"?\n\nWarning: This will set branch mapping to null for all associated leads, customers, and users.`)) return;
    try {
      const res = await apiService.deleteCrmBranch(id);
      if (res.success) fetchData();
    } catch (err) {
      alert('Error deleting branch.');
    }
  };

  const getStatusStyle = (status: string) => {
    const s = (status || 'Active').toLowerCase();
    if (s === 'active') return { background: '#D1FAE5', color: '#065F46' };
    return { background: '#FEF3C7', color: '#92400E' }; // Inactive
  };

  if (loading) return <div className="crm-loading"><div className="crm-spinner"></div> Loading Branches...</div>;

  return (
    <div>
      <div className="crm-page-header">
        <div className="crm-page-header-left">
          <h2>Branch Office Management</h2>
          <p>{branches.length} active branch locations configured</p>
        </div>
        <div>
          <button className="crm-btn crm-btn-primary" onClick={() => setShowAddForm(v => !v)}>
            {showAddForm ? '✕ Cancel' : '+ Add Branch Office'}
          </button>
        </div>
      </div>

      {/* Add Branch Form */}
      {showAddForm && (
        <form className="crm-form-panel" onSubmit={handleAddBranch}>
          <h3>➕ Configure New Branch Office</h3>
          <div className="crm-form-fields">
            <div className="crm-form-field">
              <label>Branch Name *</label>
              <input 
                type="text" 
                placeholder="e.g. New Delhi Head Office" 
                required 
                value={newBranch.name} 
                onChange={e => setNewBranch({ ...newBranch, name: e.target.value, code: e.target.value.toLowerCase().replace(/\s+/g, '-') })} 
              />
            </div>
            <div className="crm-form-field">
              <label>Branch Code (Unique) *</label>
              <input 
                type="text" 
                placeholder="e.g. delhi-head" 
                required 
                value={newBranch.code} 
                onChange={e => setNewBranch({ ...newBranch, code: e.target.value.toLowerCase().replace(/\s+/g, '-') })} 
              />
            </div>
            <div className="crm-form-field">
              <label>Location / Address</label>
              <input 
                type="text" 
                placeholder="e.g. Connaught Place, New Delhi" 
                value={newBranch.location} 
                onChange={e => setNewBranch({ ...newBranch, location: e.target.value })} 
              />
            </div>
            <div className="crm-form-field">
              <label>Status *</label>
              <select value={newBranch.status} onChange={e => setNewBranch({ ...newBranch, status: e.target.value })}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className="crm-form-actions">
            <button type="submit" className="crm-btn crm-btn-success" disabled={saving}>
              {saving ? 'Saving…' : '✓ Create Branch'}
            </button>
          </div>
        </form>
      )}

      {/* Edit Branch Form */}
      {editingBranch && (
        <form className="crm-form-panel" style={{ border: '2px solid var(--crm-primary-light)' }} onSubmit={handleUpdateBranch}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>✏️ Edit Branch: {editingBranch.name}</h3>
            <button type="button" onClick={() => setEditingBranch(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>✕</button>
          </div>
          <div className="crm-form-fields">
            <div className="crm-form-field">
              <label>Branch Name *</label>
              <input 
                type="text" 
                required 
                value={editingBranch.name || ''} 
                onChange={e => setEditingBranch({ ...editingBranch, name: e.target.value })} 
              />
            </div>
            <div className="crm-form-field">
              <label>Branch Code *</label>
              <input 
                type="text" 
                required 
                value={editingBranch.code || ''} 
                onChange={e => setEditingBranch({ ...editingBranch, code: e.target.value.toLowerCase().replace(/\s+/g, '-') })} 
              />
            </div>
            <div className="crm-form-field">
              <label>Location / Address</label>
              <input 
                type="text" 
                value={editingBranch.location || ''} 
                onChange={e => setEditingBranch({ ...editingBranch, location: e.target.value })} 
              />
            </div>
            <div className="crm-form-field">
              <label>Status *</label>
              <select value={editingBranch.status || 'Active'} onChange={e => setEditingBranch({ ...editingBranch, status: e.target.value })}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className="crm-form-actions">
            <button type="submit" className="crm-btn crm-btn-success" disabled={saving}>
              {saving ? 'Updating…' : '✓ Save Changes'}
            </button>
            <button type="button" className="crm-btn crm-btn-ghost" onClick={() => setEditingBranch(null)}>Cancel</button>
          </div>
        </form>
      )}

      {/* Branches Table */}
      <div className="crm-table-wrapper">
        <table className="crm-table">
          <thead>
            <tr>
              <th>Branch Code</th>
              <th>Name</th>
              <th>Location</th>
              <th>Status</th>
              <th>Created At</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {branches.map((branch: any) => {
              const statusStyle = getStatusStyle(branch.status);
              return (
                <tr key={branch.id}>
                  <td style={{ fontWeight: 700, color: 'var(--crm-text-primary)' }}>
                    <code>{branch.code}</code>
                  </td>
                  <td style={{ fontWeight: 600 }}>{branch.name}</td>
                  <td>{branch.location || '—'}</td>
                  <td>
                    <span className="crm-status-badge" style={{ background: statusStyle.background, color: statusStyle.color }}>
                      {branch.status || 'Active'}
                    </span>
                  </td>
                  <td style={{ color: 'var(--crm-text-secondary)', fontSize: '13px' }}>
                    {new Date(branch.created_at).toLocaleDateString('en-IN')}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button className="crm-btn crm-btn-ghost" onClick={() => setEditingBranch(branch)} style={{ padding: '4px 8px', fontSize: 12 }}>
                        ✏️ Edit
                      </button>
                      <button className="crm-btn crm-btn-ghost" onClick={() => handleDeleteBranch(branch.id, branch.name)} style={{ padding: '4px 8px', fontSize: 12, color: 'var(--crm-danger)' }}>
                        🗑 Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {branches.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '30px 10px', color: 'var(--crm-text-muted)' }}>
                  No branches configured yet. Click "+ Add Branch Office" to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BranchManagement;
